import { Alert, Button, Checkbox, Input, Modal, Select } from 'antd';
import { Profile } from 'oidc-client';
import React, { useState } from 'react';
import { ActionOption, ModuleName } from '../../../../core/models/enum';
import { isUserAllowed } from '../../../../helpers/CheckPermissionHelper';
import { formatDateOnly } from '../../../../helpers/DateHelper';
import { ApiException, MergePersonCommand, PersonsClient, PersonVm } from '../../../../utils/api';

interface Props {
    userProfile: Profile;
    person: PersonVm;
    history: { push: (path: string) => void };
}

const displayName = (person: PersonVm): string => `${person.name} ${person.surname}`.trim();

const rolesOf = (person: PersonVm): string => {
    const roles = [
        person.playerId ? 'player' : null,
        person.coachId ? 'coach' : null,
        person.officialId ? 'official' : null,
    ].filter((role): role is string => role != null);

    return roles.length > 0 ? roles.join(', ') : 'no player, coach, or official role';
};

const profilePath = (person: PersonVm): string | null => {
    if (person.playerId) {
        return `/players/${person.playerId}`;
    }
    if (person.coachId) {
        return `/coaches/${person.coachId}`;
    }
    if (person.officialId) {
        return `/officials/${person.officialId}`;
    }
    return null;
};

const errorText = (error: unknown): string => {
    if (error instanceof ApiException && error.response) {
        try {
            const parsed = JSON.parse(error.response) as { error?: string };
            if (parsed.error) {
                return parsed.error;
            }
        } catch {
            return error.response;
        }
    }

    return 'Merge failed.';
};

const MergePersonButton: React.FC<Props> = ({ userProfile, person, history }) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<'pick' | 'confirm'>('pick');
    const [options, setOptions] = useState<PersonVm[]>([]);
    const [searching, setSearching] = useState(false);
    const [target, setTarget] = useState<PersonVm | null>(null);
    const [understood, setUnderstood] = useState(false);
    const [typedSurname, setTypedSurname] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const canMerge = isUserAllowed(userProfile, ModuleName.Persons, ActionOption.Delete, person.id);
    if (!canMerge) {
        return null;
    }

    const reset = (): void => {
        setOpen(false);
        setStep('pick');
        setOptions([]);
        setTarget(null);
        setUnderstood(false);
        setTypedSurname('');
        setSaveError(null);
    };

    const search = async (input: string): Promise<void> => {
        if (input.trim().length < 2) {
            setOptions([]);
            return;
        }

        setSearching(true);
        try {
            const persons = await new PersonsClient().searchAll(input.trim());
            setOptions(persons.filter((item) => item.id !== person.id));
        } finally {
            setSearching(false);
        }
    };

    const surnameMatches =
        typedSurname.trim().toLowerCase() === (person.surname || '').trim().toLowerCase();

    const runMerge = async (): Promise<void> => {
        if (!target) {
            return;
        }

        setSaving(true);
        setSaveError(null);
        try {
            const merged = await new PersonsClient().merge(
                person.id,
                new MergePersonCommand({ id: person.id, targetPersonId: target.id })
            );
            const path = profilePath(merged) || profilePath(target);
            reset();
            if (path) {
                history.push(path);
            }
        } catch (error) {
            setSaveError(errorText(error));
        } finally {
            setSaving(false);
        }
    };

    const askFinalConfirm = (): void => {
        if (!target || !understood || !surnameMatches) {
            return;
        }

        Modal.confirm({
            title: `Delete ${displayName(person)}?`,
            content: `${displayName(
                target
            )} will keep their name and take the player, coach, and official history. ${displayName(
                person
            )} will be deleted. This cannot be undone.`,
            okText: 'Yes, merge and delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => runMerge(),
        });
    };

    return (
        <>
            <Button
                danger
                style={{ marginRight: 10, width: 100 }}
                onClick={(): void => {
                    reset();
                    setOpen(true);
                }}
            >
                Merge
            </Button>
            <Modal
                title="Merge into another person"
                visible={open}
                width={640}
                destroyOnClose
                onCancel={reset}
                okText={step === 'pick' ? 'Continue' : 'Merge'}
                okButtonProps={{
                    danger: step === 'confirm',
                    disabled:
                        step === 'pick'
                            ? !target
                            : !target || !understood || !surnameMatches || saving,
                    loading: saving,
                }}
                onOk={(): void => {
                    if (step === 'pick') {
                        setStep('confirm');
                        return;
                    }
                    askFinalConfirm();
                }}
            >
                {step === 'pick' && (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="This deletes the profile you have open."
                            description={`${displayName(
                                person
                            )} is removed. The person you choose keeps their own name and birth date, and takes this profile's player matches, teams, clubs, coach appointments, and official appointments (referee, delegate, and the other official roles). If both people already have the same role, the chosen person's role is the one that remains.`}
                        />
                        <div style={{ marginBottom: 8 }}>
                            Search by name and surname, then choose who should keep the history.
                        </div>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Name Surname"
                            filterOption={false}
                            loading={searching}
                            value={target ? target.id : undefined}
                            onSearch={(value): void => {
                                void search(value);
                            }}
                            onChange={(id: number): void => {
                                const pool =
                                    target && !options.some((item) => item.id === target.id)
                                        ? [target, ...options]
                                        : options;
                                setTarget(pool.find((item) => item.id === id) || null);
                            }}
                            notFoundContent={
                                searching ? 'Searching...' : 'Type at least 2 characters'
                            }
                        >
                            {(target && !options.some((item) => item.id === target.id)
                                ? [target, ...options]
                                : options
                            ).map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                    {displayName(item)}
                                    {item.birthDate
                                        ? `, ${formatDateOnly(item.birthDate)}`
                                        : ''}
                                    {` (${rolesOf(item)})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </>
                )}
                {step === 'confirm' && target && (
                    <>
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Check this carefully. It cannot be undone."
                            description={
                                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                                    <li>
                                        Open profile deleted: {displayName(person)} (
                                        {rolesOf(person)})
                                    </li>
                                    <li>
                                        Profile that remains: {displayName(target)} (
                                        {rolesOf(target)})
                                    </li>
                                    <li>
                                        Name and birth date stay as {displayName(target)}. Empty
                                        phone, email, or photo on that profile are filled from this
                                        one only when they are blank.
                                    </li>
                                    <li>
                                        Player, coach, and official history moves onto{' '}
                                        {displayName(target)}. A shared match, team, or club keeps
                                        their existing row.
                                    </li>
                                </ul>
                            }
                        />
                        <Checkbox
                            checked={understood}
                            onChange={(event): void => setUnderstood(event.target.checked)}
                        >
                            I understand {displayName(person)} will be deleted.
                        </Checkbox>
                        <div style={{ marginTop: 16, marginBottom: 8 }}>
                            Type the surname <strong>{person.surname}</strong> to continue.
                        </div>
                        <Input
                            value={typedSurname}
                            onChange={(event): void => setTypedSurname(event.target.value)}
                            placeholder={person.surname}
                        />
                        {saveError && (
                            <Alert
                                type="error"
                                showIcon
                                style={{ marginTop: 16 }}
                                message={saveError}
                            />
                        )}
                    </>
                )}
            </Modal>
        </>
    );
};

export default MergePersonButton;
